import { base, TABLES, FIELDS } from '../config';

export class AssessmentSubmissionModel {
  static get table() {
    if (!base) {
      throw new Error('Airtable is not configured');
    }
    return base(TABLES.AI_ASSESSMENTS);
  }

  static fields = FIELDS.AI_ASSESSMENTS;

  static async create(data) {
    try {
      // Check if Airtable is configured
      if (!base) {
        console.error('Airtable is not configured');
        throw new Error('Unable to submit assessment. Please try again later.');
      }

      // Generate submission ID
      const submissionId = `ASM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const today = new Date().toISOString().split('T')[0];

      // No longer need to filter "Other" - it's a valid value now

      // Log the incoming data
      console.log('Assessment Data Received:', {
        companyName: data.company_name,
        contactEmail: data.contact_email,
        currentStage: data.current_ai_maturity_stage
      });

      // Prepare the fields for Airtable - mapping form data to Airtable fields
      const fields = {
        [this.fields.SUBMISSION_ID]: submissionId,
        [this.fields.ASSESSMENT_DATE]: today,
        [this.fields.SUBMISSION_DATE]: new Date().toISOString(),
        [this.fields.CONTACT_NAME]: data.contact_name || '',
        [this.fields.CONTACT_EMAIL]: data.contact_email || '',
        [this.fields.COMPANY_NAME]: data.company_name || '',
        [this.fields.COMPANY_SIZE]: data.company_size || '',
        [this.fields.INDUSTRY]: data.industry || '',
        [this.fields.ROLE]: data.role || '',
        [this.fields.CURRENT_AI_MATURITY_STAGE]: data.current_ai_maturity_stage || '',
        [this.fields.CURRENT_AI_DESCRIPTION]: data.current_ai_description || '',
        [this.fields.AI_USAGE_SCOPE]: data.ai_usage_scope || '',
        [this.fields.EXECUTIVE_SUPPORT]: data.executive_support || '',
        [this.fields.SUCCESS_METRICS]: data.success_metrics || '',
        [this.fields.AI_STRATEGY]: data.ai_strategy || '',
        [this.fields.BUSINESS_ALIGNMENT]: data.business_alignment || '',
        [this.fields.DEDICATED_ROLES]: data.dedicated_roles || [],
        [this.fields.TRAINING_PROGRAMS]: data.training_programs || '',
        [this.fields.CHANGE_MANAGEMENT]: data.change_management || '',
        [this.fields.CHANGE_READINESS]: data.change_readiness || '',
        [this.fields.STAFF_BURDEN_CONCERNS]: data.staff_burden_concerns || [],
        [this.fields.AI_LITERACY]: data.ai_literacy || '',
        [this.fields.AI_TOOLS_IN_USE]: data.ai_tools_in_use || [],
        [this.fields.INTEGRATION_LEVEL]: data.integration_level || '',
        [this.fields.AUTOMATION_SCOPE]: data.automation_scope || '',
        [this.fields.CLOUD_PROVIDERS]: data.cloud_providers || [],
        [this.fields.ERP_SYSTEMS]: data.erp_systems || [],
        [this.fields.DEV_RESOURCES]: data.dev_resources || '',
        [this.fields.INFRASTRUCTURE_CONSTRAINTS]: data.infrastructure_constraints || [],
        [this.fields.DATA_QUALITY]: data.data_quality || '',
        [this.fields.DATA_ACCESSIBILITY]: data.data_accessibility || '',
        [this.fields.DATA_INVENTORY]: data.data_inventory || '',
        [this.fields.DATA_SECURITY]: data.data_security || [],
        [this.fields.STRUCTURED_VS_UNSTRUCTURED]: data.structured_vs_unstructured || [],
        [this.fields.DATA_CLEANSING_NEEDED]: data.data_cleansing_needed || '',
        [this.fields.CLASSIFIED_DATA_HANDLING]: data.classified_data_handling || '',
        [this.fields.SECURITY_POSTURE]: data.security_posture || '',
        [this.fields.COMPLIANCE_REQUIREMENTS]: data.compliance_requirements || [],
        // [this.fields.GOVERNANCE_FRAMEWORK]: data.governance_framework || '', // Removed - different field
        [this.fields.ETHICAL_GUIDELINES]: data.ethical_guidelines || '',
        [this.fields.RISK_ASSESSMENT]: data.risk_assessment || '',
        [this.fields.ACCOUNTABILITY]: data.accountability || '',
        [this.fields.DATA_GOVERNANCE]: data.data_governance || [],
        [this.fields.DEPLOYMENT_PREFERENCE]: data.deployment_preference || '',
        [this.fields.BUDGET_ALLOCATED]: data.budget_allocated || '',
        [this.fields.TIMELINE]: data.timeline || '',
        [this.fields.TARGET_STAGE]: data.target_stage || '',
        [this.fields.PRIORITY_USE_CASES]: data.priority_use_cases || '',
        [this.fields.PRIMARY_GOALS]: data.primary_goals || [],
        [this.fields.BIGGEST_CHALLENGES]: data.biggest_challenges || [],
        [this.fields.ADDITIONAL_COMMENTS]: data.additional_comments || '',
        [this.fields.SUCCESS_DEFINITION]: data.success_definition || '',
        [this.fields.STRATEGY_SCORE]: data.scores?.strategy || 0,
        [this.fields.GOVERNANCE_SCORE]: data.scores?.governance || 0,
        [this.fields.PEOPLE_SCORE]: data.scores?.people || 0,
        [this.fields.TECHNOLOGY_SCORE]: data.scores?.technology || 0,
        [this.fields.DATA_SCORE]: data.scores?.data || 0,
        [this.fields.OVERALL_SCORE]: data.scores?.overall || 0,
        [this.fields.RECOMMENDED_STAGE]: data.recommended_stage || '',
      };

      // Log the exact fields being sent to Airtable with VERBOSE details
      console.log('=== AIRTABLE SUBMISSION DEBUG ===');
      console.log('Submission ID:', submissionId);
      console.log('Table Name:', TABLES.AI_ASSESSMENTS);
      console.log('Base ID:', process.env.REACT_APP_AIRTABLE_BASE_ID);
      console.log('Token exists:', !!process.env.REACT_APP_AIRTABLE_TOKEN);
      
      console.log('\n=== MULTI-SELECT FIELDS (filtered) ===');
      console.log('DEDICATED_ROLES:', fields[this.fields.DEDICATED_ROLES]);
      console.log('AI_TOOLS_IN_USE:', fields[this.fields.AI_TOOLS_IN_USE]);
      console.log('COMPLIANCE_REQUIREMENTS:', fields[this.fields.COMPLIANCE_REQUIREMENTS]);
      console.log('PRIMARY_GOALS:', fields[this.fields.PRIMARY_GOALS]);
      console.log('BIGGEST_CHALLENGES:', fields[this.fields.BIGGEST_CHALLENGES]);
      
      // All "other" text fields have been removed - "Other" is now a regular checkbox option
      
      console.log('\n=== ALL FIELDS BEING SENT ===');
      console.log(JSON.stringify(fields, null, 2));
      
      console.log('\n=== CHECKING FOR EMPTY STRING VALUES ===');
      const fieldsToRemove = [];
      Object.keys(fields).forEach(key => {
        const value = fields[key];
        if (value === '' || value === null || value === undefined) {
          console.warn(`⚠️  Field "${key}" is EMPTY: "${value}" - WILL BE REMOVED`);
          fieldsToRemove.push(key);
        }
        if (Array.isArray(value) && value.length === 0) {
          console.warn(`⚠️  Field "${key}" is EMPTY ARRAY: [] - WILL BE REMOVED`);
          fieldsToRemove.push(key);
        }
      });
      
      // Remove empty fields to avoid Airtable errors
      fieldsToRemove.forEach(key => delete fields[key]);
      
      if (fieldsToRemove.length > 0) {
        console.log(`\n✂️  REMOVED ${fieldsToRemove.length} EMPTY FIELDS:`, fieldsToRemove);
      }

      console.log('\n=== ATTEMPTING AIRTABLE CREATE ===');
      const record = await this.table.create([{ fields }]);
      
      console.log('\n=== SUBMISSION SUCCESS ===');
      console.log('Record Created:', {
        id: record[0]?.id,
        createdTime: record[0]?.createdTime
      });
      
      return record;
    } catch (error) {
      console.error('\n=== SUBMISSION FAILED ===');
      console.error('Error Type:', error.constructor.name);
      console.error('Error Message:', error.message);
      console.error('Error Status Code:', error.statusCode);
      
      // Deep dive into error structure
      if (error.error) {
        console.error('\n🔍 AIRTABLE ERROR DETAILS:');
        console.error('Type:', error.error.type);
        console.error('Message:', error.error.message);
        
        // Check for field-specific errors
        if (error.error.type === 'INVALID_MULTIPLE_CHOICE_OPTIONS') {
          console.error('\n❌ INVALID CHOICE DETECTED:');
          console.error('This means a single-select or multi-select field received a value that is not in its allowed options');
          console.error('Check the ALL FIELDS BEING SENT log above to find which field has an invalid value');
          console.error('Compare against Airtable schema options');
        }
      }
      
      console.error('\n📋 FULL ERROR OBJECT:');
      console.error(JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      
      // Re-throw with more context
      const errorMsg = error.error?.message || error.message;
      throw new Error(`Failed to submit assessment: ${errorMsg}`);
    }
  }

  static async getAll() {
    try {
      if (!base) {
        throw new Error('Airtable is not configured');
      }

      const records = await this.table.select({
        sort: [{ field: this.fields.SUBMISSION_DATE, direction: 'desc' }]
      }).all();

      return records.map(record => ({
        id: record.id,
        ...record.fields
      }));
    } catch (error) {
      console.error('Error fetching assessments:', error);
      throw new Error(`Failed to fetch assessments: ${error.message}`);
    }
  }

  static async getById(id) {
    try {
      if (!base) {
        throw new Error('Airtable is not configured');
      }

      const record = await this.table.find(id);
      return {
        id: record.id,
        ...record.fields
      };
    } catch (error) {
      console.error('Error fetching assessment:', error);
      throw new Error(`Failed to fetch assessment: ${error.message}`);
    }
  }
}

